import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Alert, Button, Checkbox, DatePicker, Descriptions, Form, Input, InputNumber, Modal, Segmented, Select, Space, Table } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { API } from '@/services/api.service';
import { errorText, saveBlob } from './format';
import { municipalitySelectProps, useMunicipalityOptions } from './municipalities';

/**
 * PPP-PD return of one calculation as the XML ePorezi takes through „Učitaj
 * XML", so it is not typed in line by line. The payer comes from the
 * company; the SVP, municipality code, phone and e-mail are typed once and
 * offered again next time. The return wants surname and given name apart,
 * which the platform's one name field mixes up, so they are checked here and
 * kept on the person, as is the person's municipality (residence or single
 * permit; left empty, the company's). A late return (self-assessment, e.g.
 * tickets paid months ago) also takes the date the tax fell due and the
 * interest from the accountants' calculator; the tax order then pays both.
 * With contributions (supplementary work: PIO) the return also states the
 * interest on them: same rate and days as the tax, so it follows the tax
 * interest (interest x PIO / tax) and can be corrected by hand.
 * An amendment of a filed return names that return's ID; a reopened
 * calculation offers its own number.
 */

const FILING_GENERAL = 1;
const FILING_LATE = 3;

// Same rate and days as the tax: the tax interest scaled by PIO / tax, to the para (as the server derives it).
const pioInterestOf = (interest, totals) =>
  totals?.tax > 0 && totals?.pio > 0 ? Math.round(((Number(interest) || 0) * totals.pio * 100) / totals.tax) / 100 : 0;

export default function PppdXmlModal({ open, company, calculation: c, onClose }) {
  const { t } = useTranslation('taxReturns');
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const municipalities = useMunicipalityOptions(open);
  const filingType = Form.useWatch('filingType', form);
  const amend = Form.useWatch('amend', form);
  const hasPio = c?.totals?.pio > 0;

  const { data: settings } = useQuery({
    queryKey: ['tax-pppd-settings', company, c?._id],
    queryFn: () => API.taxReturns.getPppdSettings(company, c._id),
    enabled: Boolean(open && company && c?._id),
  });

  useEffect(() => {
    if (!open || !settings || !c) return;
    form.setFieldsValue({
      // Income is usually paid when the calculation is made; done ones keep the day they were finished.
      paymentDate: c.doneAt ? dayjs(c.doneAt) : dayjs(),
      filingType: c.filing?.type || FILING_GENERAL,
      liabilityDate: c.filing?.liabilityDate ? dayjs(c.filing.liabilityDate) : null,
      interest: c.filing?.interest || 0,
      interestPio: c.filing?.interestPio ?? pioInterestOf(c.filing?.interest, c.totals),
      amend: Boolean(c.filing?.amendedReturnId),
      amendedReturnId: c.filing?.amendedReturnId || c.returnNumber || '',
      incomeCode: settings.incomeCodes?.[c.kind] || '',
      municipalityCode: settings.municipalityCode,
      phone: settings.phone,
      email: settings.email,
      names: (settings.names || []).map((n) => ({ surname: n.surname, givenName: n.givenName, municipalityCode: n.municipalityCode })),
    });
  }, [open, settings, c, form]);

  const mutation = useMutation({
    mutationFn: (v) =>
      API.taxReturns.exportCalculationPppdXml(company, c._id, {
        paymentDate: v.paymentDate.format('YYYY-MM-DD'),
        filingType: v.filingType,
        liabilityDate: v.filingType === FILING_LATE ? v.liabilityDate.format('YYYY-MM-DD') : null,
        interest: v.filingType === FILING_LATE ? v.interest || 0 : 0,
        interestPio: v.filingType === FILING_LATE && hasPio ? v.interestPio ?? null : 0,
        amendedReturnId: v.amend ? (v.amendedReturnId || '').replace(/\s+/g, '') : null,
        incomeCode: v.incomeCode.trim(),
        municipalityCode: v.municipalityCode,
        phone: v.phone || '',
        email: v.email || '',
        names: (v.names || []).map((n) => ({
          surname: (n.surname || '').trim(),
          givenName: (n.givenName || '').trim(),
          municipalityCode: n.municipalityCode || null,
        })),
      }),
    onSuccess: ({ blob, fileName }) => {
      saveBlob(blob, fileName);
      queryClient.invalidateQueries({ queryKey: ['tax-pppd-settings', company] });
      // The filing type and interest are kept on the calculation and change its tax order.
      queryClient.invalidateQueries({ queryKey: ['tax-calculation', company] });
      queryClient.invalidateQueries({ queryKey: ['tax-calculations', company] });
      onClose();
    },
  });

  const missingIds = c ? c.recipients.filter((r) => !r.idNumber).map((r) => r.name) : [];
  const payer = settings?.payer;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={t('pppd.title')}
      width={860}
      destroyOnClose
      footer={
        <Space>
          <Button shape="round" onClick={onClose}>
            {t('pppd.cancel')}
          </Button>
          <Button
            shape="round"
            type="primary"
            icon={<DownloadOutlined />}
            loading={mutation.isPending}
            disabled={missingIds.length > 0}
            onClick={() => form.submit()}
          >
            {t('pppd.download')}
          </Button>
        </Space>
      }
    >
      <p className="mb-3 text-sm opacity-70">{t('pppd.help')}</p>
      {payer && (
        <Descriptions size="small" column={1} bordered className="mb-4">
          <Descriptions.Item label={t('pppd.payer')}>{payer.name}</Descriptions.Item>
          <Descriptions.Item label={t('pppd.pibRegistry')}>
            {payer.pib || '—'} / {payer.registryNumber || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('pppd.address')}>{payer.address || '—'}</Descriptions.Item>
        </Descriptions>
      )}
      {missingIds.length > 0 && (
        <Alert type="warning" showIcon className="mb-3" message={t('pppd.missingIds', { names: missingIds.join(', ') })} />
      )}
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => mutation.mutate(v)}
        onValuesChange={(changed) => {
          // A new tax interest carries the PIO interest along; a hand correction stays until the tax interest changes again.
          if ('interest' in changed && hasPio) form.setFieldValue('interestPio', pioInterestOf(changed.interest, c.totals));
        }}
      >
        <Form.Item name="filingType" label={t('pppd.filingType')} tooltip={t('pppd.filingTypeHelp')}>
          <Segmented
            options={[FILING_GENERAL, FILING_LATE].map((v) => ({ value: v, label: t(`pppd.filingTypes.${v}`) }))}
          />
        </Form.Item>
        <Space wrap align="start">
          <Form.Item name="amend" valuePropName="checked" tooltip={t('pppd.amendHelp')} label=" " colon={false}>
            <Checkbox>{t('pppd.amend')}</Checkbox>
          </Form.Item>
          {amend && (
            <Form.Item
              name="amendedReturnId"
              label={t('pppd.amendedReturnId')}
              tooltip={t('pppd.amendedReturnIdHelp')}
              rules={[{ required: true, pattern: /^\s*\d{6,15}\s*$/, message: t('errors.pppd_invalid_amended_id') }]}
            >
              <Input style={{ width: 180 }} maxLength={17} placeholder="2661132719" />
            </Form.Item>
          )}
        </Space>
        {filingType === FILING_LATE && (
          <Space wrap align="start">
            <Form.Item
              name="liabilityDate"
              label={t('pppd.liabilityDate')}
              tooltip={t('pppd.liabilityDateHelp')}
              rules={[{ required: true, message: t('errors.pppd_liability_date_required') }]}
            >
              <DatePicker format="DD.MM.YYYY" />
            </Form.Item>
            <Form.Item name="interest" label={t('pppd.interest')} tooltip={t('pppd.interestHelp')}>
              <InputNumber style={{ width: 160 }} min={0} step={0.01} precision={2} decimalSeparator="," />
            </Form.Item>
            {hasPio && (
              <Form.Item name="interestPio" label={t('pppd.interestPio')} tooltip={t('pppd.interestPioHelp')}>
                <InputNumber style={{ width: 160 }} min={0} step={0.01} precision={2} decimalSeparator="," />
              </Form.Item>
            )}
          </Space>
        )}
        <Space wrap align="start">
          <Form.Item name="paymentDate" label={t('pppd.paymentDate')} rules={[{ required: true, message: t('errors.invalid_date') }]}>
            <DatePicker format="DD.MM.YYYY" />
          </Form.Item>
          <Form.Item
            name="incomeCode"
            label={t('pppd.incomeCode')}
            tooltip={t('pppd.incomeCodeHelp')}
            rules={[{ required: true, pattern: /^\s*\d{9}\s*$/, message: t('errors.pppd_invalid_income_code') }]}
          >
            <Input style={{ width: 140 }} maxLength={11} placeholder="106603000" />
          </Form.Item>
          <Form.Item
            name="municipalityCode"
            label={t('pppd.municipalityCode')}
            tooltip={t('pppd.municipalityCodeHelp')}
            rules={[{ required: true, message: t('errors.pppd_invalid_municipality') }]}
          >
            <Select {...municipalitySelectProps} style={{ width: 220 }} options={municipalities} placeholder="013" />
          </Form.Item>
        </Space>
        <p className="mb-2 text-sm opacity-70">{t('pppd.namesHelp')}</p>
        <Form.List name="names">
          {(fields) => (
            <Table
              size="small"
              className="mb-4"
              pagination={false}
              rowKey="key"
              scroll={{ y: 320 }}
              dataSource={fields}
              columns={[
                {
                  title: t('calc.fields.recipient'),
                  render: (f) => (
                    <>
                      <div>{settings?.names?.[f.name]?.name}</div>
                      <div className="text-xs opacity-60">{settings?.names?.[f.name]?.idNumber}</div>
                    </>
                  ),
                },
                {
                  title: t('pppd.surname'),
                  render: (f) => (
                    <Form.Item name={[f.name, 'surname']} noStyle rules={[{ required: true, whitespace: true }]}>
                      <Input />
                    </Form.Item>
                  ),
                },
                {
                  title: t('pppd.givenName'),
                  render: (f) => (
                    <Form.Item name={[f.name, 'givenName']} noStyle>
                      <Input />
                    </Form.Item>
                  ),
                },
                {
                  title: t('pppd.residence'),
                  width: 220,
                  render: (f) => (
                    <Form.Item name={[f.name, 'municipalityCode']} noStyle>
                      <Select
                        {...municipalitySelectProps}
                        allowClear
                        style={{ width: '100%' }}
                        options={municipalities}
                        placeholder={t('pppd.asCompany')}
                      />
                    </Form.Item>
                  ),
                },
              ]}
            />
          )}
        </Form.List>
        <Space wrap align="start">
          <Form.Item name="phone" label={t('pppd.phone')}>
            <Input style={{ width: 180 }} />
          </Form.Item>
          <Form.Item name="email" label={t('pppd.email')}>
            <Input style={{ width: 260 }} />
          </Form.Item>
        </Space>
      </Form>
      {mutation.isError && <Alert type="error" showIcon message={errorText(t, mutation.error)} />}
    </Modal>
  );
}
